import { gql } from "@apollo/client";
import {
  ActionIcon,
  Group,
  Progress,
  SegmentedControl,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconChartBar, IconEdit, IconUsers } from "@tabler/icons-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tournament } from "../../../../gql/graphql";
import { AutoDataTable } from "../../components/AutoDataTable";
import { ListPageHeader } from "../../components/PageHeader";
import { StatusBadge } from "../../components/StatusBadge";
import { NoTournamentsState } from "../../components/EmptyState";

const deriveTournamentStatus = (tournament: {
  startAt?: string;
  endAt?: string;
  startRegistrationsAt?: string;
  endRegistrationsAt?: string;
  visibleAt?: string;
}): string => {
  const now = new Date();
  const startAt = tournament.startAt ? new Date(tournament.startAt) : null;
  const endAt = tournament.endAt ? new Date(tournament.endAt) : null;
  const startReg = tournament.startRegistrationsAt
    ? new Date(tournament.startRegistrationsAt)
    : null;
  const endReg = tournament.endRegistrationsAt
    ? new Date(tournament.endRegistrationsAt)
    : null;
  const visibleAt = tournament.visibleAt
    ? new Date(tournament.visibleAt)
    : null;

  if (endAt && now > endAt) return "finished";
  if (startAt && now >= startAt) return "live";
  if (startReg && endReg && now >= startReg && now < endReg)
    return "registration";
  if (startReg && now < startReg) return "upcoming";
  if (!visibleAt || now < visibleAt) return "draft";
  return "upcoming";
};

const formatRelativeDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return date.toLocaleDateString();
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays <= 7) return `In ${diffDays} days`;
  return date.toLocaleDateString();
};

export const TournamentList = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState("ALL");

  return (
    <div className="wp-fade-in">
      <ListPageHeader
        title="Tournaments"
        createLabel="Create Tournament"
        onCreate={() => navigate("../new")}
        showBreadcrumbs={false}
      />

      {/* Filters */}
      <Group mb="md" gap="md">
        <SegmentedControl
          value={statusFilter}
          onChange={setStatusFilter}
          data={[
            { label: "All", value: "ALL" },
            { label: "Registrations Open", value: "REGISTRATIONS_OPEN" },
            { label: "Started", value: "STARTED" },
            { label: "Ended", value: "ENDED" },
          ]}
        />
      </Group>

      <AutoDataTable
        itemPath=".."
        deleteMutation={gql`
          mutation ($id: ID!) {
            deleteTournament(id: $id)
          }
        `}
        query={gql`
          query (
            $cursor: String
            $count: Float
            $status: TournamentsQueryStatus
          ) {
            tournaments(
              page: { first: $count, after: $cursor }
              query: {
                orderBy: REGISTRATIONS_START_AT
                orderDirection: DESC
                status: $status
                showNotVisible: true
              }
            ) {
              nodes {
                id
                title
                startAt
                endAt
                startRegistrationsAt
                endRegistrationsAt
                visibleAt
                configuration {
                  teamsCount
                }
                teams(status: CONFIRMED, page: { first: 1 }) {
                  totalCount
                }
              }
              totalCount
              pageInfo {
                hasNextPage
                endCursor
              }
            }
          }
        `}
        queryVariables={{
          status: statusFilter === "ALL" ? undefined : statusFilter,
        }}
        emptyState={<NoTournamentsState onAction={() => navigate("../new")} />}
        columns={[
          {
            accessor: "title",
            title: "Title",
          },
          {
            accessor: "status",
            title: "Status",
            rawElement: (tournament: Tournament) => {
              const status = deriveTournamentStatus(tournament);
              return <StatusBadge status={status} />;
            },
          },
          {
            accessor: "teams",
            title: "Teams",
            rawElement: (tournament: Tournament) => {
              const confirmed = tournament.teams?.totalCount ?? 0;
              const max = tournament.configuration?.teamsCount ?? 0;
              const pct = max > 0 ? Math.min((confirmed / max) * 100, 100) : 0;
              return (
                <Group gap="xs">
                  <Text size="sm" fw={500}>
                    {confirmed}/{max}
                  </Text>
                  <Progress
                    value={pct}
                    color={pct >= 100 ? "green" : "cyan"}
                    size="sm"
                    radius="xl"
                    style={{ width: 60 }}
                  />
                </Group>
              );
            },
          },
          {
            accessor: "startRegistrationsAt",
            title: "Registration",
            rawElement: (tournament: Tournament) => (
              <Text size="sm">
                {tournament.startRegistrationsAt
                  ? formatRelativeDate(tournament.startRegistrationsAt)
                  : "Not set"}
              </Text>
            ),
          },
          {
            accessor: "startAt",
            title: "Start Date",
            rawElement: (tournament: Tournament) => (
              <Text size="sm">
                {tournament.startAt
                  ? new Date(tournament.startAt).toLocaleDateString()
                  : "Not set"}
              </Text>
            ),
          },
          {
            accessor: "actions",
            title: "",
            rawElement: (tournament: Tournament) => (
              <Group gap="xs">
                <Tooltip label="Manage teams">
                  <ActionIcon
                    variant="subtle"
                    onClick={() => navigate("../" + tournament.id + "/teams")}
                  >
                    <IconUsers size={16} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="View steps">
                  <ActionIcon
                    variant="subtle"
                    onClick={() => navigate("../" + tournament.id + "/steps")}
                  >
                    <IconChartBar size={16} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Edit">
                  <ActionIcon
                    variant="subtle"
                    onClick={() =>
                      navigate("../" + tournament.id + "/settings/general")
                    }
                  >
                    <IconEdit size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            ),
          },
        ]}
      />
    </div>
  );
};
